// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xCoveoState } from "@eisinc/App/CoveoConnector/Store/Reducers";
import { getFacetData, getQueryFromUrl } from "@eisinc/App/CoveoConnector/Store/Selectors/CoveoSelectors";
import { SearchEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { extractFacetData } from "@eisinc/App/CoveoConnector/Utilities/extractFacetData";
import { addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain,
    DispatchSetFilterQuery,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";

// Handler for Coveo search event tracking
export const CoveoSendTracking: typeof DispatchSetFilterQuery = props => {
    const state = props.getState() as xCoveoState;

    // Retrieve relevant properties from the application state
    const pagination = getProductListDataViewProperty(state, "pagination");
    const attributeTypeFacets = getProductListDataViewProperty(state, "attributeTypeFacets");
    const categoryFacets = getProductListDataViewProperty(state, "categoryFacets");
    const productLineFacets = getProductListDataViewProperty(state, "productLineFacets");
    const brandFacets = getProductListDataViewProperty(state, "brandFacets");
    const searchPath = getSettingsCollection(state).searchSettings.searchPath
    const isSearchPath = !!getLocation(state).pathname.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`);
    const searchHub = isSearchPath ? "search" : "listing";

    if (state.xCoveo.trackCoveo) {
        /**
         * Extract custom data for the Coveo tracking event
         * customData naming come from Coveo
         * object of {facetField, facetId, facetTitle, facetValue}
         * */
        const facetCustomData = getFacetData(props, attributeTypeFacets, categoryFacets, productLineFacets, brandFacets);
        const analyticsObject = state.xCoveo.analyticsObject;

        // Define parameters for the Coveo tracking event
        const facetSelectEventParameter: SearchEventRequest = {
            // Set event action cause based on search or facet selection
            actionCause: props.parameter.searchWithinQueries
                ? "advancedSearch"
                : props.parameter.sort
                    ? "resultsSort"
                    : "facetSelect",
            numberOfResults: facetCustomData.itemsCount || pagination?.totalItemCount,
            queryText: props.parameter.searchWithinQueries
                ? extractFacetData(props.parameter.searchWithinQueries)
                : getQueryFromUrl(state).query || "",
            responseTime: 0,
            searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
            originLevel1: searchHub,
            searchHub,
            // Attach custom data to the event
            customData: facetCustomData,
        };

        props.dispatch({
            type: "xCoveo/setfacetSelectEvent",
            facetSelectEventParameter,
        } as any);
    }
};

addToEndOfChain(chain, CoveoSendTracking);
